// Composable para operaciones de calendario
// Extrae funcionalidad de calendario de useBaseModule

import moment from "moment";
import { useI18n } from "vue-i18n";

/**
 * Composable para operaciones relacionadas con calendarios, semanas, meses y años
 */
export function useCalendar() {
  const { locale: i18nLocale } = useI18n();

  /**
   * Obtiene el nombre largo del mes en el idioma actual
   * @param month - Número del mes (1-12)
   */
  function getLongMonthName(month: number): string {
    const m = moment({ year: moment().year(), month: month - 1, date: 1 });
    const monthName = m
      .toDate()
      .toLocaleString([i18nLocale.value, "en"], { month: "long" });
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
  }

  /**
   * Obtiene el nombre del mes con el año
   * @param month - Número del mes (1-12)
   * @param year - Año completo (ej: 2025)
   */
  function getLongMonthNameWithYear(month: number, year: number): string {
    return `${getLongMonthName(month)} ${year}`;
  }

  /**
   * Obtiene el año actual
   */
  function getCurrentYear(): number {
    return parseInt(moment().format("YYYY"));
  }

  /**
   * Obtiene el mes actual (1-12)
   */
  function getCurrentMonth(): number {
    return parseInt(moment().format("M"));
  }

  /**
   * Obtiene el número de la semana actual según ISO
   */
  function getCurrentWeek(): number {
    return moment().endOf("week").isoWeek();
  }

  /**
   * Obtiene el número de semanas en un año específico según ISO
   * @param year - Año completo (ej: 2025)
   */
  function getNumOfWeeks(year: number): number {
    return moment({ year }).isoWeeksInYear();
  }

  /**
   * Obtiene el número de semana de una fecha específica
   * @param date - Día del mes (1-31)
   * @param month - Mes (1-12)
   * @param year - Año completo
   */
  function getNumOfWeek(date: number, month: number, year: number): number {
    const m = moment({ year, month: month - 1, date });
    return m.isoWeek();
  }

  /**
   * Obtiene el número de días en un mes específico
   * @param month - Mes (1-12)
   * @param year - Año completo
   */
  function getNumOfDaysInMonth(month: number, year: number): number {
    return moment({ year, month: month - 1, date: 1 }).daysInMonth();
  }

  /**
   * Obtiene el primer día del mes como Date
   * @param month - Mes (1-12)
   * @param year - Año completo
   */
  function getFirstDayOfMonth(month: number, year: number): Date {
    const date = new Date(year, month - 1, 1);
    date.setHours(12, 0, 0);
    return date;
  }

  /**
   * Obtiene el último día del mes como Date
   * @param month - Mes (1-12)
   * @param year - Año completo
   */
  function getLastDayOfMonth(month: number, year: number): Date {
    const m = moment({ year, month: month - 1, date: 1 }).endOf("month");
    const date = m.toDate();
    date.setHours(12, 0, 0);
    return date;
  }

  /**
   * Obtiene el índice del primer día de la semana (0=Domingo, 1=Lunes, etc.)
   */
  function getFirstDayOfWeekIndex(): number {
    const date = moment().startOf("week").toDate();
    return date.getDay();
  }

  /**
   * Obtiene el primer día de la primera semana ISO del año
   * @param year - Año completo
   */
  function getFirstDayOfFirstWeekInYear(year: number): Date {
    let m = moment({ year });
    // Verificar si el 1 de enero está en la primera semana del año
    if (m.week() !== 1) {
      // Si no, agregar una semana al primer día de la semana actual
      m.startOf("week").add(1, "week");
    }

    const date = m.toDate();
    date.setHours(12, 0, 0);
    return date;
  }

  /**
   * Obtiene el último día de la última semana ISO del año
   * @param year - Año completo
   */
  function getLastDayOfLastWeekInYear(year: number): Date {
    const m = moment({ year })
      .endOf("week")
      .add(getNumOfWeeks(year) - 1, "week");

    const date = m.toDate();
    date.setHours(12, 0, 0);
    return date;
  }

  /**
   * Obtiene el primer día de una semana específica
   * @param week - Número de semana ISO (1-53)
   * @param year - Año completo
   */
  function getFirstDayOfWeek(week: number, year: number): Date {
    const date = getFirstDayOfFirstWeekInYear(year);
    const m = moment(date);
    m.add(week - 1, "weeks").startOf("week");
    return m.toDate();
  }

  /**
   * Obtiene el último día de una semana específica
   * @param week - Número de semana ISO (1-53)
   * @param year - Año completo
   */
  function getLastDayOfWeek(week: number, year: number): Date {
    const date = getFirstDayOfFirstWeekInYear(year);
    const m = moment(date);
    m.add(week - 1, "weeks").endOf("week");
    return m.toDate();
  }

  /**
   * Obtiene el periodo de una semana en formato legible
   * Ejemplo: "22 octubre - 28 octubre 2025"
   * @param week - Número de semana ISO
   * @param year - Año completo
   */
  function getWeekPeriod(week: number, year: number): string {
    const firstDay = getFirstDayOfWeek(week, year);
    const lastDay = getLastDayOfWeek(week, year);

    const startPeriod =
      firstDay.getDate() +
      " " +
      getLongMonthName(parseInt(moment(firstDay).format("M")));
    const lastPeriod =
      lastDay.getDate() +
      " " +
      getLongMonthName(parseInt(moment(lastDay).format("M")));

    if (firstDay.getFullYear() !== lastDay.getFullYear()) {
      return `${startPeriod} ${firstDay.getFullYear()} - ${lastPeriod} ${lastDay.getFullYear()}`;
    } else {
      return `${startPeriod} - ${lastPeriod} ${year}`;
    }
  }

  /**
   * Añade días a una fecha
   * @param date - Fecha base
   * @param numDays - Número de días a añadir (puede ser negativo)
   */
  function addDaysToDate(date: Date, numDays: number): Date {
    return moment(date).add(numDays, "days").toDate();
  }

  /**
   * Añade meses a una fecha
   * @param date - Fecha base
   * @param numMonths - Número de meses a añadir (puede ser negativo)
   */
  function addMonthsToDate(date: Date, numMonths: number): Date {
    return moment(date).add(numMonths, "months").toDate();
  }

  /**
   * Verifica si una fecha es hoy
   * @param date - Fecha a verificar
   */
  function isToday(date: Date | null): boolean {
    if (date === null) {
      return false;
    }

    const today = new Date();

    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  /**
   * Formatea una fecha como string clave para uso en mapas
   * Formato: YYYY-MM-DD
   * @param date - Fecha a formatear
   */
  function formatDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  return {
    getLongMonthName,
    getLongMonthNameWithYear,
    getCurrentYear,
    getCurrentMonth,
    getCurrentWeek,
    getNumOfWeeks,
    getNumOfWeek,
    getNumOfDaysInMonth,
    getFirstDayOfMonth,
    getLastDayOfMonth,
    getFirstDayOfWeekIndex,
    getFirstDayOfFirstWeekInYear,
    getLastDayOfLastWeekInYear,
    getFirstDayOfWeek,
    getLastDayOfWeek,
    getWeekPeriod,
    addDaysToDate,
    addMonthsToDate,
    isToday,
    formatDateKey,
  };
}
